import React, { useEffect, useMemo, useState } from "react";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    ExclamationTriangleIcon,
    ArrowTopRightOnSquareIcon,
    FunnelIcon,
} from "@heroicons/react/24/outline";
import { getDamageHistory } from "../../api/productApi";

export default function BookHistoryTimeline({
    timeline = [],
    loading = false,
    token = "",
    onMemberClick = () => { },
    onOrderClick = () => { },
    onAddDamage = () => { },
}) {
    /* -------------------- STATES -------------------- */
    const [selectedCopyId, setSelectedCopyId] = useState("");
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [damagesMap, setDamagesMap] = useState({});
    const [damagesLoading, setDamagesLoading] = useState({});

    /* -------------------- COPY FILTER LIST -------------------- */
    const uniqueCopyIds = useMemo(() => {
        const ids = new Set();
        timeline.forEach((item) => {
            if (item.copy_id) ids.add(item.copy_id);
        });
        return Array.from(ids).sort();
    }, [timeline]);

    /* -------------------- DYNAMIC DAMAGE CHECK -------------------- */
    useEffect(() => {
        if (!timeline.length || !token) return;

        timeline.forEach(async (item) => {
            const orderId = item.order_id;
            if (damagesMap[orderId] !== undefined || damagesLoading[orderId]) return;

            setDamagesLoading((prev) => ({ ...prev, [orderId]: true }));
            try {
                const res = await getDamageHistory({ token, addedOrderId: orderId });
                const hasDmg = res?.success && Array.isArray(res.data) && res.data.length > 0;
                setDamagesMap((prev) => ({ ...prev, [orderId]: hasDmg }));
            } catch (e) {
                console.error(`Failed to fetch damage history for order ${orderId}`, e);
                setDamagesMap((prev) => ({ ...prev, [orderId]: false }));
            } finally {
                setDamagesLoading((prev) => ({ ...prev, [orderId]: false }));
            }
        });
    }, [timeline, token, damagesMap, damagesLoading]);

    /* -------------------- FILTERED TIMELINE -------------------- */
    const filteredTimeline = useMemo(() => {
        if (!selectedCopyId) return timeline;
        return timeline.filter((item) => String(item.copy_id) === String(selectedCopyId));
    }, [timeline, selectedCopyId]);

    /* -------------------- ACCORDION TOGGLE -------------------- */
    const toggleExpand = (orderId) => {
        setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
    };

    /* -------------------- DATE / DURATION UTILITY -------------------- */
    const getTransactionDates = (currentItem) => {
        let dateIssued = "-";
        let dateReturned = "-";
        let durationStr = "-";

        if (currentItem.issue_or_return === "issue") {
            dateIssued = currentItem.order_date || "-";
            // Look for matching return in the full timeline
            const returnTx = timeline.find(
                (tx) =>
                    String(tx.copy_id) === String(currentItem.copy_id) &&
                    String(tx.customer?.id) === String(currentItem.customer?.id) &&
                    tx.issue_or_return === "return" &&
                    new Date(tx.order_date) >= new Date(currentItem.order_date)
            );
            if (returnTx) {
                dateReturned = returnTx.order_date || "-";
            }
        } else {
            dateReturned = currentItem.order_date || "-";
            // Look for matching issue in the full timeline
            const issueTx = timeline.find(
                (tx) =>
                    String(tx.copy_id) === String(currentItem.copy_id) &&
                    String(tx.customer?.id) === String(currentItem.customer?.id) &&
                    tx.issue_or_return === "issue" &&
                    new Date(tx.order_date) <= new Date(currentItem.order_date)
            );
            if (issueTx) {
                dateIssued = issueTx.order_date || "-";
            }
        }

        if (dateIssued !== "-" && dateReturned !== "-") {
            const d1 = new Date(dateIssued);
            const d2 = new Date(dateReturned);
            if (!isNaN(d1) && !isNaN(d2)) {
                const diffTime = Math.abs(d2 - d1);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                durationStr = `${diffDays} days`;
            }
        }

        return { dateIssued, dateReturned, durationStr };
    };

    if (loading && timeline.length === 0) {
        return (
            <div className="flex items-center justify-center h-[200px] text-gray-400">
                Loading history…
            </div>
        );
    }

    if (timeline.length === 0) {
        return (
            <div className="text-gray-400 text-sm py-8 text-center bg-gray-50/50 rounded-lg border border-dashed">
                No history found for this book.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* FILTER BAR */}
            <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center gap-1.5 text-sm text-gray-500 font-semibold">
                    <FunnelIcon className="w-4 h-4 text-gray-400" />
                    <span>Filter by copy:</span>
                </div>
                <select
                    value={selectedCopyId}
                    onChange={(e) => setSelectedCopyId(e.target.value)}
                    className="border border-gray-200 rounded-lg px-4 py-2 text-sm bg-white outline-none cursor-pointer hover:border-gray-300 transition font-medium"
                >
                    <option value="">All copies</option>
                    {uniqueCopyIds.map((cid) => (
                        <option key={cid} value={cid}>
                            Copy ID {cid}
                        </option>
                    ))}
                </select>
            </div>

            {/* HEADER LABELS */}
            <div className="grid grid-cols-12 px-6 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50 rounded-t-lg border-x border-t border-gray-100">
                <div className="col-span-4">Member</div>
                <div className="col-span-3 text-center">Date</div>
                <div className="col-span-3 text-center">Copy ID</div>
                <div className="col-span-2 text-right pr-6">Damage</div>
            </div>

            {/* HISTORY ROWS */}
            <div className="border border-gray-100 rounded-b-lg overflow-hidden bg-white divide-y divide-gray-100">
                {filteredTimeline.map((item, index) => {
                    const customer = item.customer || {};
                    const address = item.address || {};
                    const isExpanded = expandedOrderId === item.order_id;
                    const hasDamage = damagesMap[item.order_id];
                    const isDmgLoading = damagesLoading[item.order_id];

                    const addressText = [
                        address.line1,
                        address.line2,
                        address.city,
                        address.state,
                        address.postcode,
                        address.country,
                    ]
                        .filter(Boolean)
                        .join(", ");

                    const { dateIssued, dateReturned, durationStr } = getTransactionDates(item);

                    return (
                        <div key={`${item.order_id}-${index}`} className="flex flex-col">
                            {/* ROW SUMMARY */}
                            <div
                                onClick={() => toggleExpand(item.order_id)}
                                className={`grid grid-cols-12 px-6 py-4 items-center cursor-pointer transition select-none hover:bg-gray-50/50 ${isExpanded ? "bg-blue-50/20" : ""
                                    }`}
                            >
                                {/* Member Name */}
                                <div className="col-span-4 text-sm font-medium">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onMemberClick(customer.id);
                                        }}
                                        className="text-blue-600 hover:text-blue-800 hover:underline text-left font-medium outline-none"
                                    >
                                        {customer.name || "-"}
                                    </button>
                                </div>

                                {/* Date */}
                                <div className="col-span-3 text-sm text-gray-600 text-center font-medium">
                                    {item.order_date || "-"}
                                </div>

                                {/* Copy ID */}
                                <div className="col-span-3 text-sm text-gray-600 text-center font-mono">
                                    {item.copy_id || "-"}
                                </div>

                                {/* Damage Badge + Chevron */}
                                <div className="col-span-2 flex items-center justify-end gap-3 text-right">
                                    {isDmgLoading ? (
                                        <span className="text-xs text-gray-400 animate-pulse">Checking...</span>
                                    ) : hasDamage ? (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100">
                                            Yes
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-100">
                                            N/A
                                        </span>
                                    )}

                                    {isExpanded ? (
                                        <ChevronUpIcon className="w-4 h-4 text-gray-400 shrink-0" />
                                    ) : (
                                        <ChevronDownIcon className="w-4 h-4 text-gray-400 shrink-0" />
                                    )}
                                </div>
                            </div>

                            {/* EXPANDED DETAILS */}
                            {isExpanded && (
                                <div className="px-8 py-6 bg-gray-50/30 border-t border-gray-100 flex flex-col gap-6 animate-fade-in">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* MEMBER DETAILS */}
                                        <div className="space-y-3.5">
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                Member Details
                                            </h4>
                                            <div className="space-y-2 text-sm text-gray-700">
                                                <div className="flex gap-2">
                                                    <span className="w-28 text-gray-500 font-medium">Member name</span>
                                                    <span className="text-gray-400">:</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => onMemberClick(customer.id)}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline font-semibold flex items-center gap-1"
                                                    >
                                                        {customer.name || "-"}
                                                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                                <DetailField label="Member ID" value={customer.id} />
                                                <DetailField label="Phone" value={customer.phone} />
                                                <DetailField label="Email" value={customer.email} />
                                                <DetailField label="Address" value={addressText} />
                                            </div>
                                        </div>

                                        {/* ORDER DETAILS */}
                                        <div className="space-y-3.5">
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                Order Details
                                            </h4>
                                            <div className="space-y-2 text-sm text-gray-700 font-medium">
                                                <div className="flex gap-2">
                                                    <span className="w-28 text-gray-500 font-medium">Order ID</span>
                                                    <span className="text-gray-400">:</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => onOrderClick(item.order_id)}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline font-semibold flex items-center gap-1"
                                                    >
                                                        #{item.order_id || "-"}
                                                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                                <DetailField
                                                    label="Order type"
                                                    value={
                                                        item.type === "shop_order" ? "Shop order" : item.type
                                                    }
                                                />
                                                <DetailField label="Method" value={item.delivery_method} />
                                                <DetailField label="Copy ID" value={item.copy_id} />
                                                <DetailField label="Date issued" value={dateIssued} />
                                                <DetailField label="Date returned" value={dateReturned} />
                                                <DetailField label="Duration" value={durationStr} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* FOOTER ACTIONS */}
                                    <div className="border-t border-gray-100 pt-4 flex items-center justify-between text-xs">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                onAddDamage(item, {
                                                    memberId: customer.id,
                                                    orderId: item.order_id,
                                                })
                                            }
                                            className="flex items-center gap-1.5 text-red-600 hover:text-red-800 font-bold transition outline-none"
                                        >
                                            <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                                            <span>DAMAGE RECORD</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                onAddDamage(item, {
                                                    memberId: customer.id,
                                                    orderId: item.order_id,
                                                })
                                            }
                                            className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1 outline-none"
                                        >
                                            <span>View full damage record</span>
                                            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 shrink-0" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function DetailField({ label, value }) {
    return (
        <div className="flex gap-2">
            <span className="w-28 text-gray-500 font-medium shrink-0">{label}</span>
            <span className="text-gray-400">:</span>
            <span className="text-gray-800 break-words flex-1 font-semibold">{value || "-"}</span>
        </div>
    );
}
